import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useEffect, useRef } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";

import { BookingWizardDataAtom } from "../../atoms/stores";
import { useInitializeRequestBookingWizard } from "../../hooks/useInitializeRequestBookingWizard/useInitializeRequestBookingWizard";
import { BookingWizard } from "../../types";
import { RequestWizardStepContainer } from "../WizardStepContainer/RequestWizardStepContainer";
import { BookingWizardUI } from "./BookingWizardUI";
import { RequestBookingWizardQueryRendererQuery } from "./__generated__/RequestBookingWizardQueryRendererQuery.graphql";

type RequestBookingWizardQueryRendererProps = {
  queryRef: PreloadedQuery<RequestBookingWizardQueryRendererQuery>;
  onClose: () => void;
  requestedTime: string;
  minTime?: string;
  maxTime?: string;
  queueSlotId: string;
  initializationOverides?: BookingWizard.BookingWizardInitializationOverides;
};

export const RequestBookingWizardQueryRenderer = ({
  queryRef,
  onClose,
  requestedTime,
  minTime,
  maxTime,
  queueSlotId,
  initializationOverides,
}: RequestBookingWizardQueryRendererProps) => {
  const data = usePreloadedQuery(
    RequestBookingWizardQueryRendererQueryString,
    queryRef
  );
  const bookingWizardData = useAtomValue(BookingWizardDataAtom);
  const trackEvent = useTrackEvent();
  const hasTrackedAggregateEntry = useRef(false);
  const hasRendered = useRef(false);
  const requestGroupCount =
    bookingWizardData?.type === "RequestBooking"
      ? bookingWizardData.requestGroupCount ?? 1
      : 1;

  useEffect(() => {
    if (!hasRendered.current) {
      hasRendered.current = true;
      return;
    }

    if (requestGroupCount > 1 && !hasTrackedAggregateEntry.current) {
      hasTrackedAggregateEntry.current = true;
      trackEvent("MultipleSlotRequest.Entry", {
        groupCount: requestGroupCount,
      });
    }
  }, [requestGroupCount, trackEvent]);

  useInitializeRequestBookingWizard({
    sessionRef: data.session ?? null,
    nodeRef: data.node ?? null,
    requestedTime,
    minTime,
    maxTime,
    queueSlotId,
    initializationOverides,
  });

  return (
    <BookingWizardUI
      currentServerTime={data.currentServerTime}
      uiHeaderRef={data.club ?? null}
      uiFooterRef={data.node ?? null}
      onDismiss={onClose}
      StepContainer={
        data?.node ? (
          <RequestWizardStepContainer
            nodeRef={data.node}
            addPlayersRef={data.session?.currentClientProfile ?? null}
            previousBookingsRef={data.session?.currentClientProfile ?? null}
            addPlayersPrivateRef={data.session?.currentClientProfile ?? null}
            onClose={onClose}
          />
        ) : null
      }
    />
  );
};

export const RequestBookingWizardQueryRendererQueryString = graphql`
  query RequestBookingWizardQueryRendererQuery(
    $queueSlotId: ID!
    $currentDate: Date
    $players: [ID]
    $slotType: SlotType
    $clubSlug: String!
    $bookingType: BookingType!
  ) {
    currentServerTime
    club(slug: $clubSlug) {
      ...BookingWizardUIHeaderFragment
    }
    node(id: $queueSlotId) {
      id
      ...AddPlayersStepNode
      ...useInitializeRequestBookingWizardNode
      ...RequestWizardStepContainerNode
      ...BookingWizardUIFooterFragment
    }
    session {
      ...useInitializeRequestBookingWizardSession
      currentClientProfile {
        ...AddPlayersStepClientProfile @arguments(players: $players)
        ...AddPlayersPrivateStepClientProfile
        ...PreviousBookingsStepClientProfile
      }
    }
  }
`;
